import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  approveBookingApprovalApiV1BookingApprovalsApprovalIdApprovePost,
  rejectBookingApprovalApiV1BookingApprovalsApprovalIdRejectPost,
  triggerBookingSyncApiV1BookingApprovalsSyncPost,
} from "../../api/generated/sdk.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";

/** Raise a readable error from a hey-api error response (surfaces the 400
 *  missing-fields detail so the UI can show what's blocking approval). */
const raiseIfError = (response: { error?: unknown }, prefix: string) => {
  if ("error" in response && response.error) {
    const detail = (response.error as { detail?: unknown }).detail;
    throw new Error(`${prefix}: ${JSON.stringify(detail ?? response.error)}`);
  }
};

export const useApproveBookingApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response =
        await approveBookingApprovalApiV1BookingApprovalsApprovalIdApprovePost({
          client: apiClient,
          path: { approval_id: id },
        });
      raiseIfError(response, "Failed to approve booking");
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.BOOKING_APPROVALS],
      });
    },
    onError: (error) => console.error("Error approving booking:", error),
  });
};

export const useRejectBookingApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const response =
        await rejectBookingApprovalApiV1BookingApprovalsApprovalIdRejectPost({
          client: apiClient,
          path: { approval_id: id },
          body: { reason: reason ?? null },
        });
      raiseIfError(response, "Failed to reject booking");
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.BOOKING_APPROVALS],
      });
    },
    onError: (error) => console.error("Error rejecting booking:", error),
  });
};

/** Admin "sync now" — pull new bookings from the Vinci copy DB. */
export const useTriggerBookingSync = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await triggerBookingSyncApiV1BookingApprovalsSyncPost({
        client: apiClient,
      });
      raiseIfError(response, "Failed to sync bookings");
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.BOOKING_APPROVALS],
      });
    },
    onError: (error) => console.error("Error syncing bookings:", error),
  });
};
